import React from 'react';
import { Link } from 'react-router-dom';

import Navigation from '@plone/volto/components/theme/Navigation/Navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';

import searchSVG from '@plone/volto/icons/zoom.svg';
import downloadSVG from '@plone/volto/icons/headset.svg';

import HeaderSearchForm from './HeaderSearchForm';

type HeaderProps = {
  pathname?: string;
};

const DOWNLOAD_LABEL = 'Contact Us';

const SEARCH_OPEN_EVENT = 'wildcard:search-open';
const NAV_DROPDOWN_OPEN_EVENT = 'wildcard:nav-dropdown-open';
const NAV_INTERACTION_EVENT = 'wildcard:nav-interaction';

const logoModule = require(
  '../../static/img/logos/wildcard-logo-horz-tagline-reverse-cr.png',
);

const logoSrc =
  typeof logoModule === 'string'
    ? logoModule
    : logoModule.default;

const Header: React.FC<HeaderProps> = ({ pathname = '/' }) => {
  const [searchOpen, setSearchOpen] = React.useState(false);

  React.useEffect(() => {
    const closeSearch = () => {
      setSearchOpen(false);
    };

    window.addEventListener(NAV_DROPDOWN_OPEN_EVENT, closeSearch);
    window.addEventListener(NAV_INTERACTION_EVENT, closeSearch);

    return () => {
      window.removeEventListener(NAV_DROPDOWN_OPEN_EVENT, closeSearch);
      window.removeEventListener(NAV_INTERACTION_EVENT, closeSearch);
    };
  }, []);

  const handleSearchToggle = () => {
    setSearchOpen((previousValue) => {
      const nextValue = !previousValue;

      if (nextValue) {
        window.dispatchEvent(new CustomEvent(SEARCH_OPEN_EVENT));
      }

      return nextValue;
    });
  };

  const handleNavInteraction = (
    event: React.SyntheticEvent<HTMLDivElement>,
  ) => {
    const target = event.target as HTMLElement;

    if (target.closest('nav.navigation')) {
      window.dispatchEvent(new CustomEvent(NAV_INTERACTION_EVENT));
      setSearchOpen(false);
    }
  };

  return (
    <div className="ui basic segment header-wrapper">
      <div className="ui container">
        <div className="header">
          <div className="header-left">
            <Link className="header-logoLink" aria-label="Home" to="/">
              <Image
                className="header-logoImg"
                src={logoSrc}
                alt="Wildcard"
              />

              <span className="header-brand">Wildcard</span>
            </Link>
          </div>

          <div
            className="header-right"
            onMouseDownCapture={handleNavInteraction}
            onFocusCapture={handleNavInteraction}
          >
            <Navigation pathname={pathname} />

            <button
              type="button"
              className="header-searchToggle"
              aria-label={searchOpen ? 'Close search' : 'Open search'}
              aria-expanded={searchOpen}
              aria-controls="header-search-panel"
              onClick={handleSearchToggle}
            >
              <Icon name={searchSVG} size="24px" />
            </button>

            <a
              className="header-cta white"
              href="/contact-us"
              target="_self"
              rel="noreferrer"
              aria-label={DOWNLOAD_LABEL}
            >
              <span className="header-ctaText">{DOWNLOAD_LABEL}</span>
              <Icon name={downloadSVG} size="22px" />
            </a>
          </div>

          <div
            id="header-search-panel"
            className={`header-searchPanel${searchOpen ? ' is-open' : ''}`}
            aria-hidden={!searchOpen}
            {...(!searchOpen ? { inert: '' } : {})}
          >
            <HeaderSearchForm
              autoFocus={searchOpen}
              className="header-searchForm"
              inputId="header-search-input"
              onEscape={() => setSearchOpen(false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;